// Copyright: IQGeo Limited 2010-2019
import _ from 'underscore';
import Promise from 'bluebird';
import $ from 'jquery';
import myw from 'myWorld/base/core';
import xml2json from 'xml2json';
import 'myWorld/base/errors';

myw.JsonpParseError = myw.Error.extend('JsonpParseError');

/* Defines methods that provide jsonp and tunnelled communication to http servers */
myw.DatasourceCommsMixin = {
    /*
     * Sends an external search or selection request
     * @param  {object}     requestParams   Parameters to send to the external selection server
     * @param  {string}     options.featureType
     * @param  {string}     options.urlFieldName        Name of datasource field that holds the base url
     * @param  {string}     [options.relativeUrl='']
     * @return {Promise<Array<Feature>>}  Promise for the external features
     */
    _sendFeaturesRequest(requestParams, options) {
        const asFeature = featureData => this._asFeature(featureData, options.featureType);

        return this._sendRequest(requestParams, options).then(data => {
            if (data && data.features) {
                const features = _.map(data.features, asFeature);
                return _.extend(features, { totalCount: data.totalFeatures }); //totalcount is used by the resultListControl
            } else if (data && options.format == 'xml') {
                return xml2json(data).wfs.numberOfFeatures;
            } else {
                throw new Error(
                    `Unexpected result from datasource '${
                        this.options.name
                    }' - missing 'features' property. Feature type: ${options.featureType}`
                );
            }
        });
    },

    /*
     * Sends a generic external request
     * @param  {object}     requestParams   Parameters to send to the external selection server
     * @param  {string}     options.urlFieldName        Name of datasource field that holds the base url
     * @param  {string}     [options.relativeUrl='']
     * @return {Promise}
     */
    _sendRequest(requestParams, options) {
        let request, url, baseUrl;

        _.defaults(options, { relativeUrl: '' });

        if (options.jsonp) {
            baseUrl = this.options[options.urlFieldName];
            url = options.url || baseUrl + options.relativeUrl;

            //direct JSONP request to the external server
            request = this._jsonpRequest(url, requestParams, options);
        } else {
            //request throught the myWorld server
            if (this.owner) options.owner = this.owner;
            request = this.tunnelRequest(requestParams, options);
        }

        return request;
    },

    /*
     * Performs the external selection via a JSONP request
     * @param  {string}     url
     * @param  {Object}     requestParams   Parameters for the request
     * @param  {string}     options.jsonpCallback
     * @return {Promise<json>}              Selected feature details
     */
    _jsonpRequest(url, requestParams, options) {
        const ajaxArgs = {
            dataType: 'jsonp',
            jsonp: false,
            jsonpCallback: options.jsonpCallback,
            url: url,
            data: requestParams
        };

        return this._ensureAuthenticated(url) //authenticate if necessary
            .then(() =>
                //send the selection request to the external server
                Promise.resolve($.ajax(ajaxArgs))
            )
            .catch(e => {
                switch (e.status) {
                    case 200:
                        throw new myw.JsonpParseError();
                    case 404:
                        throw new Error('HTTP Error 404: Not Found');
                    default:
                        throw new Error(e.statusText || e.name);
                }
            })
            .timeout(this.options.jsonpTimeout || 4000); //jsonp failures don't throw errors
    },

    /*
     * Authenticates the browser session with a given url
     * Uses  jsonp when sending the request to bypass cross origin issues
     * Won't work on IE because urls with user/pass are blocked for security reasons
     * Username and password will be obtained from datasource's options/spec
     * @private
     */
    _ensureAuthenticated(url) {
        //don't send request for IE as it will popup a dialog
        if (this.options.username && !L.Browser.ie) {
            if (!this._authenticationPromise) this._authenticationPromise = {};
            const key = url;
            if (!this._authenticationPromise[key]) {
                const i = url.indexOf('://') + 3;
                url = `${url.slice(0, i) + this.options.username}:${
                    this.options.password
                }@${url.slice(i)}`;

                const ajaxArgs = {
                    dataType: 'jsonp',
                    jsonp: false,
                    jsonpCallback: this.options.jsonpCallback,
                    url: url
                };

                this._authenticationPromise[key] = this._ajax(ajaxArgs) // Promise.resolve($.ajax(ajaxArgs))
                    .timeout(4000) //authentication method failure on IE doesn't throw any errors
                    .catch(e => {
                        //We expect XML for this request, so an error is thrown in that situation
                        if (e.code == 200) {
                            //the configuration is working. error was to XML instead of script
                            return 'success';
                        } else if (e.name === 'TimeoutError') {
                            throw new myw.AuthenticationError('Timeout');
                        } else {
                            const error = new myw.AuthenticationError(
                                `Status code was ${e.status}`
                            );
                            error.code = e.status;
                            error.responseText = e.responseText || e.statusText;
                            throw error;
                        }
                    });
            }
            return this._authenticationPromise[key];
        } else {
            return Promise.resolve();
        }
    },

    _ajax(args) {
        return new Promise((resolve, reject) => {
            $.ajax(
                _.extend(
                    {
                        complete: resolve,
                        error(e) {
                            const error = new Error(`Status code was ${e.status}`);
                            error.code = e.status;
                            error.responseText = e.responseText;
                            reject(error);
                        }
                    },
                    args
                )
            );
        });
    }
};

export default myw.DatasourceCommsMixin;
